'use client';

import { Fragment, memo, Suspense, useEffect, useRef, useState } from 'react';
import { IconCircleCheckFilled, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useSearchParams } from 'next/navigation';
import { CarSelectionForm, VehicleType } from '@/components/ui/LandingPageForm/CarSelectionForm';
import { RadioButton } from '@/components/ui/RadioButton/RadioButton';
import { Select } from '@/components/ui/Select/Select';
import { trackCtaClick } from '@/utils/tracking';
import { useExperiments } from '@/components/ExperimentsContext';
import { getExperimentBucket, REMOVE_DURATION_SELECTOR_FLAG_KEY } from '@/domain/experiments';
import {
  FIXED_DURATION_ONE_DAY,
  FIXED_DURATION_ONE_WEEK,
  FIXED_DURATION_TWO_DAYS,
  resolveDurationParam,
} from '@/domain/duration';
import styles from './LandingPageForm.module.css';
import Card from '@tempcover/bolt-ui/card';

export const LANDING_PAGE_FORM_TEST_ID = 'landing-page-form';

type DayInputMode = 'radio' | 'dropdown';

const DAY_INPUT_MODE_RADIO: DayInputMode = 'radio';
const DAY_INPUT_MODE_DROPDOWN: DayInputMode = 'dropdown';

// Re-exported for the Builder registries and existing importers; the values now
// live in @/domain/duration alongside the rest of the accepted durations.
export { FIXED_DURATION_ONE_DAY, FIXED_DURATION_TWO_DAYS, FIXED_DURATION_ONE_WEEK };

// Name for the visible duration controls. Deliberately NOT "duration", so the
// picker can never collide with the hidden input below. It still needs *a* name
// to keep the radios in one group, so onSubmit strips it just before the browser
// serialises the form — otherwise `duration_choice` would ride along in the
// outgoing query string alongside `duration`.
const DURATION_CHOICE_FIELD = 'duration_choice';

const DURATION_TABS = ['hours', 'days', 'weeks'] as const;
type DurationTab = (typeof DURATION_TABS)[number];

// 'days' + 2 -> '2 days'; 'days' + 1 -> '1 day'. Matches the "<n> <unit>" wire
// format motor-journeys parses.
const durationValue = (tab: DurationTab, amount: number) => `${amount} ${tab.slice(0, -1)}${amount > 1 ? 's' : ''}`;

const QUICK_PICK_DURATIONS: readonly string[] = [
  FIXED_DURATION_ONE_DAY,
  FIXED_DURATION_TWO_DAYS,
  FIXED_DURATION_ONE_WEEK,
];

/** '10 days' -> { amount: 10, tab: 'days' }; undefined if it isn't a duration. */
const parseDuration = (duration?: string): { amount: number; tab: DurationTab } | undefined => {
  const [amount, unit] = duration?.split(' ') ?? [];
  const tab = unit && DURATION_TABS.find((candidate) => candidate === `${unit.replace(/s$/, '')}s`);

  return tab && Number.isFinite(Number(amount)) ? { amount: Number(amount), tab } : undefined;
};

/**
 * Which unit tab has to be open for `duration` to be visible on screen.
 *
 * The quick-pick row already shows 1 day / 2 days / 1 week, so those need no
 * tab. Anything else only exists inside its unit tab, and leaving it closed
 * would submit a duration with nothing selected on screen.
 */
const initialDurationTab = (duration: string | undefined, daysOnly: boolean): DurationTab | '' => {
  if (daysOnly) return 'days';
  if (!duration || QUICK_PICK_DURATIONS.includes(duration)) return '';

  return parseDuration(duration)?.tab ?? '';
};

/** The days radio row only reaches 7; longer durations live in the dropdown. */
const initialDayInputMode = (duration?: string): DayInputMode => {
  const parsed = parseDuration(duration);

  return parsed?.tab === 'days' && parsed.amount > 7 ? DAY_INPUT_MODE_DROPDOWN : DAY_INPUT_MODE_RADIO;
};

interface DurationAmountsProps {
  tab: DurationTab | '';
  daysOnly: boolean;
  dayInputMode: DayInputMode;
  onToggleDayInputMode: () => void;
  selected?: string;
  onSelect: (duration: string) => void;
}

// Defined at module scope on purpose. It used to be created inside
// LandingPageFormInner's body, which made it a new component type on every
// render, remounting the radios and losing the visitor's pick whenever any other
// field changed.
const DurationAmounts = memo(function DurationAmounts({
  tab,
  daysOnly,
  dayInputMode,
  onToggleDayInputMode,
  selected,
  onSelect,
}: DurationAmountsProps) {
  // Returns the radios themselves rather than a wrapper, so they stay direct
  // flex children of each case's container and wrap as they did before.
  const amountRadios = (count: number, unitTab: DurationTab) =>
    Array.from(Array(count)).map((_, idx) => {
      const value = durationValue(unitTab, idx + 1);
      return (
        <RadioButton
          key={`custom_duration_${idx}`}
          name={DURATION_CHOICE_FIELD}
          value={value}
          checked={selected === value}
          onClick={() => onSelect(value)}
          label={String(idx + 1)}
          variant="secondary"
          className="w-10!"
          inputClassName={styles.radioField}
        />
      );
    });

  switch (tab) {
    case 'hours':
      return (
        <div className="mx-auto mb-5 flex w-[70%] flex-wrap justify-center gap-1 md:w-[55%]">
          {amountRadios(12, 'hours')}
        </div>
      );
    case 'days':
      return (
        <div className="mx-auto mb-5 flex w-[60%] flex-wrap justify-center gap-1 md:w-[90%]">
          {dayInputMode === DAY_INPUT_MODE_RADIO ? (
            amountRadios(7, 'days')
          ) : (
            <Select
              tabIndex={0}
              name={DURATION_CHOICE_FIELD}
              className="w-[303px] max-w-[303px]"
              value={selected}
              onChange={(e) => onSelect(e.target.value)}
            >
              {Array.from(Array(21)).map((_, idx) => (
                <option key={`custom_duration_${idx + 8}`} value={durationValue('days', idx + 8)}>
                  {idx + 8}
                </option>
              ))}
            </Select>
          )}
          {daysOnly === false && (
            <button
              onClick={(e) => {
                e.preventDefault();
                onToggleDayInputMode();
              }}
              className={`bg-neutral border-secondary-400 hover:primary-100 flex w-10 items-center justify-center rounded border-1 py-2 text-center text-sm uppercase ${dayInputMode === DAY_INPUT_MODE_RADIO ? 'bg-neutral hover:bg-background-300 text-primary border-secondary-400' : 'bg-secondary text-neutral'}`}
              aria-label="Change Input"
            >
              {dayInputMode === DAY_INPUT_MODE_RADIO ? (
                <IconChevronDown width={20} height={20} className="text-secondary" />
              ) : (
                <IconChevronUp width={20} height={20} className="text-neutral" />
              )}
            </button>
          )}
        </div>
      );
    case 'weeks':
      return <div className="mx-auto mb-5 flex w-[80%] flex-wrap justify-center gap-1">{amountRadios(4, 'weeks')}</div>;
    default:
      return null;
  }
});

export type JourneyType = 'none' | 'learner' | 'expat' | 'impounded' | 'courier';

interface LandingPageFormProps {
  heading?: string;
  subHeading?: string;
  regLabel: string;
  showRegLabel?: boolean;
  showRegToggle?: boolean;
  defaultDurationType?: string;
  daysOnly?: boolean;
  useBanner?: boolean;
  bannerCoverType?: string;
  bannerCoverDuration?: string;
  checkboxLabel?: string;
  useCardStyle?: boolean;
  formIntroText?: string;
  submitButtonText?: string;
  submitButtonColor?: 'orange' | 'purple';
  journey: JourneyType;
  regPlateOnly: boolean;
  showDurationPicker?: boolean;
  // Code-driven only — not exposed via LandingPageForm.registry.ts. Defaults to true
  // (own-brand) for TCEX-2988's duration-removal rollout; the affiliate/co-brand
  // PartnershipFormPanel block explicitly opts out by passing false.
  enableDurationRemoval?: boolean;
}

const LandingPageFormInner = ({
  heading,
  subHeading,
  regLabel,
  showRegLabel = true,
  showRegToggle = true,
  defaultDurationType,
  daysOnly = false,
  useBanner = false,
  bannerCoverType,
  bannerCoverDuration,
  checkboxLabel,
  useCardStyle = false,
  formIntroText = 'To get started simply enter your reg...',
  submitButtonText = 'Continue',
  submitButtonColor = 'orange',
  journey,
  regPlateOnly,
  showDurationPicker = true,
  enableDurationRemoval = true,
}: LandingPageFormProps) => {
  const { experiments, hasAffiliateRef } = useExperiments();
  const durationRemovalBucket = getExperimentBucket(experiments, REMOVE_DURATION_SELECTOR_FLAG_KEY);
  const hideDuration =
    enableDurationRemoval && durationRemovalBucket === 'variant' && !hasAffiliateRef && journey !== 'impounded';

  // The page's configured duration, or nothing when it implies none — see
  // @/domain/duration for why an absent param is the right answer rather than a
  // fallback. Used only to seed the state below.
  const configuredDuration =
    resolveDurationParam(defaultDurationType) ?? (daysOnly ? FIXED_DURATION_ONE_DAY : undefined);

  // Both of these are seeded from the configured duration so the selector opens
  // showing it. Anything else submits a duration the visitor cannot see.
  const [durationTab, setDurationTab] = useState<DurationTab | ''>(() =>
    initialDurationTab(configuredDuration, daysOnly)
  );
  const [dayInputMode, setDayInputMode] = useState<DayInputMode>(() => initialDayInputMode(configuredDuration));
  const [selectedDuration, setSelectedDuration] = useState<string | undefined>(configuredDuration);
  const [accepted, setAccepted] = useState(false);
  const [showError, setShowError] = useState(false);

  const [reg, setReg] = useState<string>('');
  const [type, setType] = useState<VehicleType | ''>('');

  const Wrapper = useCardStyle ? Card : Fragment;
  const wrapperProps = useCardStyle ? { className: 'md:mx-auto md:max-w-[440px] p-6' } : {};

  const formRef = useRef<HTMLFormElement | null>(null);

  const selectTab = (tab: DurationTab) => {
    setDurationTab(tab);
    // Opening a tab visibly pre-selects its first amount, so submit that too —
    // the visitor must never be sent a duration other than the one they can see.
    setSelectedDuration(durationValue(tab, 1));
  };

  return (
    <div className="mx-auto md:max-w-[800px]">
      {heading && <h1 className="mx-auto mb-4! text-left md:text-center">{heading}</h1>}
      {subHeading && <div className="tc-subheading mt-0 mb-8 text-left md:text-center">{subHeading}</div>}

      <Wrapper {...wrapperProps}>
        {formIntroText && <p className="text-dark mb-4 tracking-[0.12px]">{formIntroText}</p>}
        <form
          ref={formRef}
          noValidate
          action={process.env.NEXT_PUBLIC_MOTOR_URL}
          data-component-id={LANDING_PAGE_FORM_TEST_ID}
          onSubmit={(e) => {
            if (!accepted && checkboxLabel) {
              setShowError(true);
              e.preventDefault();
              return;
            }

            const willSubmit = formRef.current!.checkValidity();

            if (!willSubmit) {
              e.stopPropagation();
              e.preventDefault();
            }

            window.dataLayer = window.dataLayer || [];

            // old event for rebrand
            window.dataLayer.push({
              event: 'amplitude_event',
              event_name: 'form_submitted',
              event_properties: {
                journey,
                path: window.location.pathname,
              },
            });

            let source = 'direct';
            if (window?.location?.search && window.location.search.includes('utm_source')) {
              source = new URLSearchParams(window.location.search).get('utm_source')!;
            }

            // new event
            window.dataLayer.push({
              event: 'amplitude_event',
              event_name: 'registration_submitted',
              event_properties: {
                product_type: type,
                registration: reg,
                source: source,
              },
            });

            // The form data set is built after this handler runs, so dropping
            // the name here keeps the picker's own controls out of the query
            // string while leaving the hidden `duration` input to carry the
            // value. Guarded on willSubmit: on a prevented submit the controls
            // must keep their name, or the radios stop being one group and more
            // than one option can look selected on the retry.
            if (willSubmit) {
              formRef.current
                ?.querySelectorAll(`[name="${DURATION_CHOICE_FIELD}"]`)
                .forEach((el) => el.removeAttribute('name'));
            }
          }}
        >
          {journey != 'none' && <input type="hidden" name={journey} value="true" />}
          <input type="hidden" name="tp" value="23212" />
          <input type="hidden" name="auth-interstitial" value="true" />

          {/* useSearchParams() suspends in Next 15 SSR — isolate it here so only
              these hidden inputs are deferred, not the entire form. */}
          <Suspense>
            <SearchParamInputs />
          </Suspense>

          <div className={`${styles.fieldContainer}`}>
            <CarSelectionForm
              className="mb-4"
              regLabel={regLabel}
              showRegLabel={showRegLabel}
              showRegToggle={showRegToggle}
              journey={journey}
              reg={reg}
              type={type}
              setReg={setReg}
              setType={setType}
            />
            {!regPlateOnly && !useBanner && !hideDuration && showDurationPicker && (
              <>
                {!daysOnly && (
                  <fieldset className="mb-6">
                    <legend className={`tc-label mb-1 pl-px`}>Can we guess how long you need cover for?</legend>
                    <div className="gap-regular flex">
                      <RadioButton
                        name={DURATION_CHOICE_FIELD}
                        disabled={false}
                        checked={selectedDuration === FIXED_DURATION_ONE_DAY}
                        onClick={() => {
                          setDurationTab('');
                          setSelectedDuration(FIXED_DURATION_ONE_DAY);
                        }}
                        value="1 day"
                        label="1 day"
                        inputClassName={styles.radioField}
                      />
                      <RadioButton
                        name={DURATION_CHOICE_FIELD}
                        disabled={false}
                        checked={selectedDuration === FIXED_DURATION_TWO_DAYS}
                        onClick={() => {
                          setDurationTab('');
                          setSelectedDuration(FIXED_DURATION_TWO_DAYS);
                        }}
                        value="2 days"
                        label="2 days"
                        inputClassName={styles.radioField}
                      />
                      <RadioButton
                        name={DURATION_CHOICE_FIELD}
                        disabled={false}
                        checked={selectedDuration === FIXED_DURATION_ONE_WEEK}
                        onClick={() => {
                          setDurationTab('');
                          setSelectedDuration(FIXED_DURATION_ONE_WEEK);
                        }}
                        value="1 week"
                        label="1 week"
                        inputClassName={styles.radioField}
                      />
                    </div>
                  </fieldset>
                )}

                <fieldset className="mb-3">
                  <legend className={`tc-label mb-1 pl-px`}>
                    {daysOnly ? 'Choose your duration below' : 'Maybe not, choose your own duration'}
                  </legend>
                  <div className="gap-regular mb-5 flex justify-center">
                    {DURATION_TABS.filter((tab) => !daysOnly || tab === 'days').map((tab) => (
                      <RadioButton
                        key={tab}
                        name={DURATION_CHOICE_FIELD}
                        disabled={false}
                        onClick={() => selectTab(tab)}
                        checked={durationTab === tab}
                        label={tab === 'hours' ? 'Hours' : tab === 'days' ? 'Days' : 'Weeks'}
                        variant="secondary"
                      />
                    ))}
                  </div>

                  <DurationAmounts
                    tab={durationTab}
                    daysOnly={daysOnly}
                    dayInputMode={dayInputMode}
                    onToggleDayInputMode={() => {
                      const nextMode =
                        dayInputMode === DAY_INPUT_MODE_RADIO ? DAY_INPUT_MODE_DROPDOWN : DAY_INPUT_MODE_RADIO;
                      setDayInputMode(nextMode);
                      setSelectedDuration(durationValue('days', nextMode === DAY_INPUT_MODE_DROPDOWN ? 8 : 1));
                    }}
                    selected={selectedDuration}
                    onSelect={setSelectedDuration}
                  />
                </fieldset>
              </>
            )}

            {/* The only control named `duration`, so it is the only thing that can
                reach motor-journeys. Rendered whenever we have a duration to send —
                the page's configured default, or whatever the visitor picked above —
                including when the selector is hidden by the remove-duration-selector
                variant (TCEX-2988), which previously dropped the param altogether.
                Omitted entirely when there is no duration, leaving the journey to
                apply its own default. See @/domain/duration. */}
            {!regPlateOnly && selectedDuration && <input type="hidden" name="duration" value={selectedDuration} />}
          </div>

          {!regPlateOnly && useBanner && (
            <div className="bg-primary text-neutral mx-auto mt-8 mb-3 flex w-fit items-center gap-2 rounded p-4 align-middle">
              <IconCircleCheckFilled width={40} height={40} className="text-neutral mx-auto" />
              <div className="flex-1 px-10 text-center uppercase">
                <p className="mb-0 text-2xl">{bannerCoverDuration}</p>
                <p className="mt-0">{bannerCoverType}</p>
              </div>
            </div>
          )}

          {checkboxLabel && (
            <div className={`mx-auto my-6 flex w-auto gap-2 p-2 ${showError ? 'border-error text-error border' : ''}`}>
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => {
                  setAccepted(e.target.checked);
                  setShowError(false);
                }}
                required
                value="yes"
              />
              <p className="mb-3 text-center text-lg">{checkboxLabel}</p>
            </div>
          )}

          <button
            type="submit"
            onClick={() => trackCtaClick(submitButtonText, 'form_submit')}
            className={
              submitButtonColor === 'purple' ? 'tc-submit-button tc-submit-button--purple' : 'tc-submit-button'
            }
          >
            {submitButtonText}
          </button>
        </form>
      </Wrapper>
    </div>
  );
};

/**
 * Renders URL search params as hidden form inputs so they are forwarded
 * on form submission. Deferred to client-only so the server always
 * produces the same HTML regardless of which query params are present,
 * preventing hydration mismatches when the CDN serves cached responses.
 */
function SearchParamInputs() {
  const searchParams = useSearchParams();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <>
      {[...searchParams.entries()].map(([k, v], idx) => (
        <input key={k + v + idx} type="hidden" name={k} value={v} />
      ))}
    </>
  );
}

export const LandingPageForm = (props: LandingPageFormProps) => {
  return <LandingPageFormInner {...props} />;
};
